import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Skeleton, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api/axios';
import { useProject } from '../context/ProjectContext';
import { Ornament } from '../components/Brand';

function ProjectDialog({ open, onClose, onSave, initial }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleEnter = () => {
    setName(initial?.name || '');
    setDescription(initial?.description || '');
    setImageUrl(initial?.imageUrl || '');
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/upload', fd);
      setImageUrl(data.url);
    } catch { } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle>{initial ? 'Edit project' : 'New project'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Project name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus fullWidth />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={2} fullWidth placeholder="What is this project about?" />
          <Box>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImage} />
            {imageUrl ? (
              <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', height: 140 }}>
                <img src={imageUrl} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <Tooltip title="Remove image">
                  <IconButton size="small" onClick={() => setImageUrl('')}
                    sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(18,18,18,0.85)', '&:hover': { bgcolor: 'rgba(18,18,18,1)' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Button variant="outlined" size="small" color="inherit"
                startIcon={uploading ? <CircularProgress size={14} /> : <ImageIcon fontSize="small" />}
                onClick={() => fileRef.current?.click()} disabled={uploading}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                {uploading ? 'Uploading…' : 'Add cover image'}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="text" color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!name.trim()}
          onClick={() => { if (name.trim()) onSave({ name: name.trim(), description, imageUrl }); }}
        >
          {initial ? 'Save' : 'Create project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ProjectCard({ project, onSelect, onEdit, onDelete }) {
  return (
    <Box onClick={() => onSelect(project)}
      sx={{
        border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
        bgcolor: 'background.paper', cursor: 'pointer', position: 'relative',
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: 'rgba(235,231,225,0.24)' },
        '&:hover .card-actions': { opacity: 1 },
      }}
    >
      <Box sx={{ height: 96, bgcolor: '#1f1d1b', position: 'relative' }}>
        {project.imageUrl ? (
          <img src={project.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', opacity: 0.45 }}>
            <Ornament size={26} />
          </Box>
        )}
        <Box className="card-actions"
          sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 0.15s' }}
          onClick={(e) => e.stopPropagation()}
        >
          {[[EditIcon, 'Edit project', onEdit], [DeleteIcon, 'Delete project', onDelete]].map(([Icon, label, fn]) => (
            <Tooltip key={label} title={label}>
              <IconButton onClick={() => fn(project)}
                sx={{ width: 26, height: 26, bgcolor: 'rgba(18,18,18,0.85)', '&:hover': { bgcolor: 'rgba(18,18,18,1)' } }}
              >
                <Icon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Box>
      <Box sx={{ px: 1.75, py: 1.5 }}>
        <Typography fontWeight={600} noWrap>{project.name}</Typography>
        <Typography variant="caption" color="text.secondary"
          sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5, minHeight: '3em' }}
        >
          {project.description || 'No description'}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Projects() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { selectProject } = useProject();
  const [dialog, setDialog] = useState({ open: false, project: null });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => (Array.isArray(r.data) ? r.data : [])),
  });

  const createProject = useMutation({
    mutationFn: (data) => api.post('/projects', data).then((r) => r.data),
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      setDialog({ open: false, project: null });
      selectProject(project);
      navigate('/app/dashboard');
    },
  });

  const updateProject = useMutation({
    mutationFn: ({ id, ...data }) => api.put(`/projects/${id}`, data).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }); setDialog({ open: false, project: null }); },
  });

  const deleteProject = useMutation({
    mutationFn: (id) => api.delete(`/projects/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }); setDeleteConfirm(null); },
  });

  const handleSave = (data) => {
    if (dialog.project) updateProject.mutate({ id: dialog.project.id, ...data });
    else createProject.mutate(data);
  };

  return (
    <Box>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5">Projects</Typography>
            <Typography variant="body2" color="text.secondary">Open a project to see its dashboards</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, project: null })}>
            New project
          </Button>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={170} />)}
          </Box>
        ) : projects.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography fontWeight={600} sx={{ mb: 0.5 }}>No projects yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create the first one to get started</Typography>
            <Button variant="outlined" color="inherit" startIcon={<AddIcon />} sx={{ borderColor: 'divider' }}
              onClick={() => setDialog({ open: true, project: null })}
            >
              Create project
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p}
                onSelect={(proj) => { selectProject(proj); navigate('/app/dashboard'); }}
                onEdit={(proj) => setDialog({ open: true, project: proj })}
                onDelete={setDeleteConfirm}
              />
            ))}
          </Box>
        )}
      </Box>

      <ProjectDialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, project: null })}
        onSave={handleSave}
        initial={dialog.project}
      />

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete project?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete <strong>{deleteConfirm?.name}</strong> and all its dashboards? This can’t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit">Cancel</Button>
          <Button variant="contained" color="error" onClick={() => deleteProject.mutate(deleteConfirm.id)} disabled={deleteProject.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
